import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import EmailHealthWidget from "./EmailHealthWidget";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DollarSign,
  Package,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  RotateCcw,
  ShoppingCart,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

interface Order {
  id: string;
  order_type: string;
  status: string;
  amount_cents: number;
  created_at: string;
}

const COLORS = ["hsl(var(--primary))", "hsl(var(--champagne))", "hsl(var(--muted-foreground))", "hsl(var(--destructive))", "#94a3b8"];

const OrderAnalytics = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("orders")
        .select("id, order_type, status, amount_cents, created_at")
        .order("created_at", { ascending: false })
        .limit(1000);
      setOrders((data as Order[]) || []);
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalRevenue = orders.filter((o) => !["cancelled", "refunded"].includes(o.status)).reduce((s, o) => s + o.amount_cents, 0);
  const totalOrders = orders.length;
  const activeOrders = orders.filter((o) => o.status === "active").length;
  const pendingOrders = orders.filter((o) => o.status === "pending").length;
  const refundedTotal = orders.filter((o) => o.status === "refunded").reduce((s, o) => s + o.amount_cents, 0);

  // Status breakdown for pie chart
  const statusCounts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const pieData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

  // Monthly revenue for bar chart (last 6 months)
  const monthlyData: Record<string, number> = {};
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = d.toLocaleString("default", { month: "short", year: "2-digit" });
    monthlyData[key] = 0;
  }
  orders
    .filter((o) => !["cancelled", "refunded"].includes(o.status))
    .forEach((o) => {
      const d = new Date(o.created_at);
      const key = d.toLocaleString("default", { month: "short", year: "2-digit" });
      if (key in monthlyData) monthlyData[key] += o.amount_cents;
    });

  const barData = Object.entries(monthlyData).map(([month, cents]) => ({
    month,
    revenue: cents / 100,
  }));

  const kpis = [
    { label: "Total Revenue", value: `$${(totalRevenue / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`, icon: DollarSign, color: "text-primary" },
    { label: "Total Orders", value: totalOrders, icon: ShoppingCart, color: "text-foreground" },
    { label: "Active", value: activeOrders, icon: CheckCircle, color: "text-primary" },
    { label: "Pending", value: pendingOrders, icon: Clock, color: "text-champagne" },
    { label: "Refunded", value: `$${(refundedTotal / 100).toFixed(2)}`, icon: RotateCcw, color: "text-destructive" },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon size={16} className={k.color} />
              </div>
              <p className="font-display text-2xl text-foreground">{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <TrendingUp size={16} /> Monthly Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            {barData.every((d) => d.revenue === 0) ? (
              <p className="text-center text-muted-foreground py-12 text-sm">No revenue data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, "Revenue"]}
                  />
                  <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Package size={16} /> Order Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length === 0 ? (
              <p className="text-center text-muted-foreground py-12 text-sm">No orders yet</p>
            ) : (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {pieData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="capitalize">{d.name}</span>
                      <Badge variant="secondary" className="text-xs ml-auto">{d.value}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Health Widget */}
      <EmailHealthWidget />
    </div>
  );
};

export default OrderAnalytics;
